import { PlusIcon, StarIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import useMediaQuery from 'src/shared/hooks/useMediaQuery';
import { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { isImageMimeType } from 'src/shared/utils/fileTypeUtils';

import { cn, Progress, Spinner } from 'src/shared/components/ui';
import { ButtonV2, Input, Typography } from 'src/shared/components/ui';

import { updateRfqFileCaption } from '../../api/updateRfqFileCaption';
import { uploadRfqFileFromUrl } from '../../api/uploadRfqFileFromUrl';
import { useCaptionModal } from '../../hooks/useCaptionModal';
import { useFileCaptionManager } from '../../hooks/useFileCaptionManager';
import { useFilePreview } from '../../hooks/useFilePreview';
import useImageSearch from '../../hooks/useImageSearch';
import { useOptimisticFileUploadWithCaption } from '../../hooks/useOptimisticFileUploadWithCaption';
import BlobV2 from '../BlobV2';
import { InlineCaptionOverlay } from './InlineCaptionOverlay';
import { UploadFailedNotice } from './UploadFailedNotice';

interface AddReferencesImageTabProps {
  /** Whether this tab is currently active and visible */
  isActive: boolean;
  rfq?: Rfq;
  /** Callback when files are selected and ready to submit */
  onFilesSelected: (files: UploadedFile[]) => void;
  onClose: () => void;
}

interface ImageSearchResult {
  url: string;
  thumbnail_url?: string;
  [key: string]: unknown;
}

interface SelectedImage {
  url: string;
  thumbnailUrl?: string;
  id: string;
  file?: File;
  pendingFileId: string;
  uploadedFileId?: string;
  uploadedFile?: UploadedFile;
  caption: string;
  originalIndex: number;
  isUploading?: boolean;
  uploadError?: string;
  preview?: string;
  source: 'user' | 'reference';
}

/**
 * Image search tab content for the AddReferencesModal.
 * Allows searching for reference images, selecting them, adding captions, and submitting.
 * Adapted from RfqImageSelectionDesktop for use in a modal context (builder mode only).
 */
const AddReferencesImageTab = ({
  isActive,
  rfq,
  onFilesSelected,
  onClose
}: AddReferencesImageTabProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [currentSearchQuery, setCurrentSearchQuery] = useState<
    string | undefined
  >(undefined);
  const [images, setImages] = useState<ImageSearchResult[]>([]);
  const [showMoreInput, setShowMoreInput] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(
    new Set()
  );
  const [loadedImageUrls, setLoadedImageUrls] = useState<Set<string>>(
    new Set()
  );
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([]);
  const [pendingImage, setPendingImage] = useState<SelectedImage | null>(null);
  const [pendingFileUploads, setPendingFileUploads] = useState<SelectedImage[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastRequestTimeRef = useRef<number>(0);
  const MIN_REQUEST_INTERVAL = 2000;
  const MIN_IMAGE_WIDTH = 100;
  const MIN_IMAGE_HEIGHT = 100;
  const selectedImagesRef = useRef<SelectedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputWrapperRef = useRef<HTMLDivElement>(null);
  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const captionListRef = useRef<HTMLDivElement>(null);

  // Internal search term suffix (not shown to user)
  const SEARCH_TERM_SUFFIX = 'product photography aesthetic';
  const isFirstSearchRef = useRef(true);

  // Reusable hooks
  const queryClient = useQueryClient();
  const { setPreview, getPreview, removePreview, previewUrlsRef } =
    useFilePreview();
  const uploadHook = useOptimisticFileUploadWithCaption({
    rfqId: rfq?.rfq?.id
  });
  const {
    pendingFiles,
    addFiles: addFilesToUpload,
    removeFile: removeFileFromUpload,
    waitForUploads,
    isUploading: isUploadingFiles,
    uploadFileWithCaption,
    retryUpload
  } = uploadHook;
  const { getPendingFileByIdOrFile, updateCaptionForFile } =
    useFileCaptionManager(rfq?.rfq?.id, uploadHook);

  // Combine selectedImages with pendingImage and pendingFileUploads for modal display
  const modalItems = useMemo(() => {
    if (pendingImage) {
      return [pendingImage];
    }
    if (pendingFileUploads.length > 0) {
      return pendingFileUploads;
    }

    return selectedImages;
  }, [selectedImages, pendingImage, pendingFileUploads]);

  const captionModal = useCaptionModal(modalItems);
  const {
    isCaptionModalOpen,
    onCloseCaptionModal,
    currentCaptionIndex,
    captionValue,
    setCaptionValue,
    isEditMode,
    handleEditCaption,
    handleCaptionNext: captionNext,
    handleCaptionPrevious: captionPrevious,
    handleCaptionDone: captionDone,
    openModalForNewItems
  } = captionModal;

  const imageSearch = useImageSearch(currentSearchQuery);

  // Reset all state when modal closes
  const resetState = useCallback(() => {
    setSelectedImages([]);
    setPendingImage(null);
    setPendingFileUploads([]);
    setShowMoreInput(false);
    setSearchQuery('');
    setRateLimitError(null);
    setCaptionValue('');
    isFirstSearchRef.current = true;
  }, [setCaptionValue]);

  // Helper to scroll images container to top
  const scrollToTop = useCallback(() => {
    if (!imagesContainerRef.current) return;

    let scrollableElement: HTMLElement | null = imagesContainerRef.current;

    while (scrollableElement) {
      const hasScrollableContent =
        scrollableElement.scrollHeight > scrollableElement.clientHeight;
      const overflowY = window.getComputedStyle(scrollableElement).overflowY;

      if (
        hasScrollableContent &&
        (overflowY === 'auto' || overflowY === 'scroll')
      ) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });

        return;
      }
      scrollableElement = scrollableElement.parentElement;
    }
  }, []);

  // Helper to check if a selected image is an actual image (not a document file)
  const isImageFile = (selectedImage: SelectedImage): boolean => {
    if (selectedImage.file) {
      return isImageMimeType(selectedImage.file.type);
    }

    return true;
  };

  // Keep refs in sync
  useEffect(() => {
    selectedImagesRef.current = selectedImages;
    selectedImages.forEach((img) => {
      if (img.preview && !previewUrlsRef.current.has(img.id)) {
        setPreview(img.id, img.preview);
      }
    });
  }, [selectedImages, setPreview]);

  // Sync query results with images state
  useEffect(() => {
    if (imageSearch.data) {
      setImages(imageSearch.data);
      setFailedImageUrls(new Set());
      const newImageUrls = new Set(
        imageSearch.data.map((img) => (typeof img === 'string' ? img : img.url))
      );

      setLoadedImageUrls((prev) => {
        const kept = new Set<string>();

        prev.forEach((url) => {
          if (newImageUrls.has(url)) {
            kept.add(url);
          }
        });

        return kept;
      });
      setRateLimitError(null);
      setTimeout(scrollToTop, 100);
    } else if (imageSearch.isError) {
      setImages([]);
      setRateLimitError(null);
    }
  }, [imageSearch.data, imageSearch.isError, scrollToTop]);

  // Memoize fetchImages to prevent recreation
  const fetchImages = useCallback((query: string) => {
    if (!query.trim()) return;

    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTimeRef.current;

    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;

      setRateLimitError(
        `Please wait ${Math.ceil(waitTime / 1000)} second(s) before searching again`
      );

      return;
    }

    lastRequestTimeRef.current = now;
    setRateLimitError(null);
    setUserSearchQuery(query);

    if (isFirstSearchRef.current) {
      setCurrentSearchQuery(`${query} ${SEARCH_TERM_SUFFIX}`);
      isFirstSearchRef.current = false;
    } else {
      setCurrentSearchQuery(query);
    }
  }, []);

  // Sync selectedImages with pendingFiles
  const pendingFilesMap = useMemo(() => {
    const map = new Map();

    pendingFiles.forEach((pf) => {
      if (pf.id) map.set(pf.id, pf);
      if (pf.file) {
        const key = `${pf.file.name}-${pf.file.size}-${pf.file.lastModified}`;

        map.set(key, pf);
      }
    });

    return map;
  }, [pendingFiles]);

  useEffect(() => {
    setSelectedImages((prevImages) => {
      if (prevImages.length === 0) return prevImages;

      const existingPendingIds = new Set(
        prevImages
          .filter((img) => img.pendingFileId)
          .map((img) => img.pendingFileId!)
      );
      const newPendingFiles = pendingFiles.filter(
        (pf) => !existingPendingIds.has(pf.id)
      );

      let hasChanges = false;
      const result: SelectedImage[] = [];

      for (const img of prevImages) {
        const previewUrl = getPreview(img.id) || img.preview;

        if (previewUrl && !previewUrlsRef.current.has(img.id)) {
          setPreview(img.id, previewUrl);
        }

        let updatedImage: SelectedImage = {
          ...img,
          preview: previewUrl || img.preview
        };

        if (!updatedImage.pendingFileId && img.file) {
          const matchingPendingFile = newPendingFiles.find(
            (pf) =>
              pf.file === img.file ||
              (pf.file.name === img.file!.name &&
                pf.file.size === img.file!.size &&
                pf.file.lastModified === img.file!.lastModified)
          );

          if (matchingPendingFile) {
            hasChanges = true;
            updatedImage = {
              ...updatedImage,
              pendingFileId: matchingPendingFile.id
            };
          }
        }

        if (updatedImage.pendingFileId) {
          const pendingFile = pendingFilesMap.get(updatedImage.pendingFileId);

          if (pendingFile && pendingFile.caption !== updatedImage.caption) {
            hasChanges = true;
            updatedImage = {
              ...updatedImage,
              caption: pendingFile.caption
            };
          }
        }

        result.push(updatedImage);
      }

      return hasChanges ? result : prevImages;
    });
  }, [pendingFilesMap, pendingFiles, getPreview, setPreview, previewUrlsRef]);

  /** Whether the user has performed at least one search */
  const hasSearched =
    images.length > 0 ||
    (currentSearchQuery !== undefined && !imageSearch.isError);

  const handleCloseRefineSearch = useCallback(() => {
    if (!hasSearched) return;
    setShowMoreInput(false);
  }, [hasSearched]);

  const handleOpenRefineSearch = useCallback(() => {
    setSearchQuery(userSearchQuery);
    setShowMoreInput(true);
  }, [userSearchQuery]);

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      fetchImages(searchQuery);
      setShowMoreInput(false);
    }
  }, [searchQuery, fetchImages]);

  // Focus search input when the tab becomes active and overlay is shown
  useEffect(() => {
    if (isActive && showMoreInput) {
      setTimeout(() => {
        const input = searchInputWrapperRef.current?.querySelector('input');

        input?.focus();
      }, 150);
    }
  }, [isActive, showMoreInput]);

  // Handle ESC key to close refine search (only after initial search)
  useEffect(() => {
    if (!showMoreInput || !hasSearched) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseRefineSearch();
        setTimeout(() => {
          imagesContainerRef.current?.focus();
        }, 0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showMoreInput, hasSearched, handleCloseRefineSearch]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleImageError = useCallback((imageUrl: string) => {
    setFailedImageUrls((prev) => {
      if (prev.has(imageUrl)) return prev;

      return new Set(prev).add(imageUrl);
    });
  }, []);

  const handleImageLoad = useCallback((imageUrl: string) => {
    setLoadedImageUrls((prev) => {
      if (prev.has(imageUrl)) return prev;

      return new Set(prev).add(imageUrl);
    });
  }, []);

  const handleImageLoadOrSizeCheck = useCallback(
    (imageUrl: string, e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;

      if (
        img.naturalWidth < MIN_IMAGE_WIDTH ||
        img.naturalHeight < MIN_IMAGE_HEIGHT
      ) {
        handleImageError(imageUrl);
      } else {
        handleImageLoad(imageUrl);
      }
    },
    [handleImageError, handleImageLoad]
  );

  // Check for cached images after images change
  useEffect(() => {
    if (images.length === 0) return;

    const rafId = requestAnimationFrame(() => {
      const container = imagesContainerRef.current;

      if (!container) return;

      const imgElements = container.querySelectorAll('img');

      imgElements.forEach((img) => {
        if (img.complete && img.naturalHeight > 0 && img.src) {
          if (
            img.naturalWidth >= MIN_IMAGE_WIDTH &&
            img.naturalHeight >= MIN_IMAGE_HEIGHT
          ) {
            handleImageLoad(img.src);
          } else {
            handleImageError(img.src);
          }
        }
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [images, handleImageLoad, handleImageError]);

  // Helper to upload image from URL via backend
  const uploadImageFromUrl = async (
    url: string,
    caption: string
  ): Promise<UploadedFile> => {
    if (!rfq?.rfq?.id) {
      throw new Error('RFQ ID is required');
    }

    const response = await uploadRfqFileFromUrl({
      rfq_id: rfq.rfq.id,
      image_url: url,
      caption: caption || undefined
    });

    return response.file;
  };

  const scrollCaptionListToBottom = useCallback(() => {
    if (captionListRef.current) {
      setTimeout(() => {
        if (captionListRef.current) {
          captionListRef.current.scrollTo({
            top: captionListRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);

  const handleImageClick = (
    e: React.MouseEvent,
    image: ImageSearchResult,
    index: number
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const imageUrl = typeof image === 'string' ? image : image.url;
    const isSelected = selectedImages.some((img) => img.url === imageUrl);

    if (isSelected) {
      const imageToRemove = selectedImages.find((img) => img.url === imageUrl);

      if (imageToRemove) {
        if (imageToRemove.pendingFileId) {
          removeFileFromUpload(imageToRemove.pendingFileId);
        }
        setSelectedImages((prev) =>
          prev.filter((img) => img.id !== imageToRemove.id)
        );
      }
    } else {
      const imageId = `${imageUrl}-${Date.now()}-${Math.random()}`;
      const newPendingImage: SelectedImage = {
        url: imageUrl,
        thumbnailUrl: image.thumbnail_url,
        id: imageId,
        pendingFileId: '',
        caption: '',
        originalIndex: index,
        source: 'reference'
      };

      setPendingImage(newPendingImage);
      handleEditCaption(0);
    }
  };

  const handleRemoveSelectedImage = useCallback(
    (imageId: string) => {
      const imageToRemove = selectedImagesRef.current.find(
        (img) => img.id === imageId
      );

      if (!imageToRemove) return;

      removePreview(imageToRemove.id);

      if (imageToRemove.pendingFileId) {
        removeFileFromUpload(imageToRemove.pendingFileId);
      }

      setSelectedImages((prev) => {
        const filtered = prev.filter((img) => img.id !== imageId);

        if (filtered.length === 0) {
          onCloseCaptionModal();
        } else if (currentCaptionIndex >= filtered.length) {
          captionModal.setCurrentCaptionIndex(filtered.length - 1);
        }

        return filtered;
      });
    },
    [
      removeFileFromUpload,
      removePreview,
      onCloseCaptionModal,
      currentCaptionIndex,
      captionModal
    ]
  );

  // Helper to convert and save caption
  const convertAndSaveCaption = async (
    image: SelectedImage,
    caption: string
  ) => {
    // If already uploaded via URL, update caption via PATCH endpoint
    if (image.uploadedFileId) {
      if (!rfq?.rfq?.id) {
        throw new Error('RFQ ID is required');
      }

      try {
        await updateRfqFileCaption({
          rfq_id: rfq.rfq.id,
          file_id: parseInt(image.uploadedFileId, 10),
          caption: caption || ''
        });

        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, caption, isUploading: false, uploadError: undefined }
              : img
          )
        );
      } catch (error) {
        console.error('Error updating file caption:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update caption';

        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, isUploading: false, uploadError: errorMessage }
              : img
          )
        );
        throw error;
      }

      return;
    }

    // Upload image from URL via backend
    if (!image.file) {
      let uploadedFile: UploadedFile | null = null;
      let lastError: Error | unknown = null;

      try {
        uploadedFile = await uploadImageFromUrl(image.url, caption);
      } catch (error) {
        console.error('Error uploading image from main URL:', error);
        lastError = error;
      }

      if (!uploadedFile && image.thumbnailUrl) {
        try {
          uploadedFile = await uploadImageFromUrl(image.thumbnailUrl, caption);
          lastError = null;
        } catch (fallbackError) {
          console.error(
            'Error uploading image from thumbnail URL:',
            fallbackError
          );
          lastError = fallbackError;
        }
      }

      if (uploadedFile) {
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                  ...img,
                  uploadedFileId: uploadedFile!.id.toString(),
                  uploadedFile: uploadedFile!,
                  caption,
                  isUploading: false,
                  uploadError: undefined
                }
              : img
          )
        );
      } else {
        const errorMessage =
          lastError instanceof Error
            ? lastError.message
            : 'Failed to upload image';

        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? { ...img, isUploading: false, uploadError: errorMessage }
              : img
          )
        );
        throw lastError;
      }
    } else if (image.file) {
      const pendingFile = getPendingFileByIdOrFile(
        image.pendingFileId,
        image.file
      );

      if (pendingFile) {
        await updateCaptionForFile(pendingFile, caption).catch(() => {});
      }
    }
  };

  // Add pending image/file to selectedImages and start upload
  const addPendingImage = useCallback(async () => {
    if (pendingImage) {
      const imageToUpload = {
        ...pendingImage,
        caption: captionValue,
        isUploading: true
      };

      setSelectedImages((prev) => [...prev, imageToUpload]);
      setPendingImage(null);

      convertAndSaveCaption(imageToUpload, captionValue).catch(() => {});
      scrollCaptionListToBottom();

      return;
    }

    if (pendingFileUploads.length > 0) {
      const currentPendingFile = pendingFileUploads[currentCaptionIndex];

      if (!currentPendingFile || !currentPendingFile.file) return;

      const fileToAdd = currentPendingFile.file;

      addFilesToUpload([fileToAdd]);

      const imageToAdd = {
        ...currentPendingFile,
        caption: captionValue
      };

      setSelectedImages((prev) => [...prev, imageToAdd]);
      setPendingFileUploads((prev) =>
        prev.filter((_, index) => index !== currentCaptionIndex)
      );

      const updatePendingFileCaption = async (retries = 5) => {
        for (let i = 0; i < retries; i++) {
          const pendingFile = getPendingFileByIdOrFile(undefined, fileToAdd);

          if (pendingFile) {
            await updateCaptionForFile(pendingFile, captionValue).catch(
              () => {}
            );
            if (!captionValue.trim() && pendingFile.status === 'pending') {
              await uploadFileWithCaption(pendingFile.id, captionValue).catch(
                () => {}
              );
            }

            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      };

      updatePendingFileCaption();
      scrollCaptionListToBottom();
    }
  }, [
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    scrollCaptionListToBottom,
    addFilesToUpload,
    getPendingFileByIdOrFile,
    updateCaptionForFile,
    uploadFileWithCaption
  ]);

  // Caption save handler
  const handleCaptionSave = useCallback(async () => {
    if (pendingImage || pendingFileUploads.length > 0) {
      return;
    }

    const currentImage = selectedImages[currentCaptionIndex];

    if (!currentImage) return;

    if (currentImage.file) {
      const pendingFile = getPendingFileByIdOrFile(
        currentImage.pendingFileId,
        currentImage.file
      );

      if (pendingFile) {
        await updateCaptionForFile(pendingFile, captionValue).catch(() => {});
      }
    } else {
      const needsUpload = !currentImage.uploadedFileId;

      if (needsUpload) {
        setSelectedImages((prev) =>
          prev.map((img) =>
            img.id === currentImage.id
              ? { ...img, isUploading: true, uploadError: undefined }
              : img
          )
        );
        convertAndSaveCaption(currentImage, captionValue).catch(() => {});
        await new Promise((resolve) => setTimeout(resolve, 100));
      } else if (currentImage.uploadedFileId) {
        await convertAndSaveCaption(currentImage, captionValue).catch(() => {});
      }
    }
  }, [
    selectedImages,
    pendingImage,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    getPendingFileByIdOrFile,
    updateCaptionForFile
  ]);

  const handleCaptionNext = useCallback(async () => {
    if (pendingImage) {
      await addPendingImage();
      onCloseCaptionModal();

      return;
    }

    if (pendingFileUploads.length > 0) {
      const hasMoreFiles = pendingFileUploads.length > 1;

      await addPendingImage();
      if (hasMoreFiles) {
        setCaptionValue('');
      } else {
        setPendingFileUploads([]);
        onCloseCaptionModal();
      }

      return;
    }

    await handleCaptionSave();
    await captionNext();
  }, [
    handleCaptionSave,
    captionNext,
    pendingImage,
    pendingFileUploads,
    addPendingImage,
    onCloseCaptionModal,
    setCaptionValue
  ]);

  const handleCaptionPrevious = useCallback(async () => {
    await handleCaptionSave();
    await captionPrevious();
  }, [handleCaptionSave, captionPrevious]);

  const handleCaptionDone = useCallback(async () => {
    if (pendingImage || pendingFileUploads.length > 0) {
      await addPendingImage();
      setPendingFileUploads([]);
      onCloseCaptionModal();

      return;
    }

    await handleCaptionSave();
    captionDone();
  }, [
    handleCaptionSave,
    captionDone,
    pendingImage,
    pendingFileUploads,
    addPendingImage,
    onCloseCaptionModal
  ]);

  const handleCaptionModalClose = useCallback(async () => {
    if (pendingImage) {
      setPendingImage(null);
      setCaptionValue('');
    }
    if (pendingFileUploads.length > 0) {
      pendingFileUploads.forEach((file) => {
        if (file.id) {
          removePreview(file.id);
        }
      });
      setPendingFileUploads([]);
      setCaptionValue('');
    }
    onCloseCaptionModal();
  }, [
    pendingImage,
    pendingFileUploads,
    onCloseCaptionModal,
    setCaptionValue,
    removePreview
  ]);

  /** Submit selected images back to the parent via callback */
  const handleSend = async () => {
    if (selectedImages.length === 0) {
      onClose();

      return;
    }

    if (!rfq?.rfq?.id) return;

    // Upload any images without uploadedFileId
    const imagesToUpload = selectedImages.filter(
      (img) => !img.uploadedFileId && !img.file
    );

    if (imagesToUpload.length > 0) {
      try {
        for (const image of imagesToUpload) {
          const uploadedFile = await uploadImageFromUrl(
            image.url,
            image.caption
          );

          setSelectedImages((prev) =>
            prev.map((img) =>
              img.id === image.id
                ? {
                    ...img,
                    uploadedFileId: uploadedFile.id.toString(),
                    uploadedFile
                  }
                : img
            )
          );
        }
      } catch (error) {
        console.error('Error uploading images:', error);

        return;
      }
    }

    // Save any pending caption
    if (isCaptionModalOpen && selectedImages[currentCaptionIndex]) {
      await handleCaptionSave();
    }

    setIsSubmitting(true);

    try {
      // Wait for file uploads to complete
      await waitForUploads();

      const hasErrors = pendingFiles.some((pf) => pf.status === 'error');

      if (hasErrors) {
        setIsSubmitting(false);

        return;
      }

      // Collect UploadedFile objects from both sources
      const urlUploadedFiles = selectedImages
        .filter((img) => img.uploadedFile)
        .map((img) => img.uploadedFile!);
      const fileUploadedFiles = pendingFiles
        .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
        .map((pf) => pf.uploadedFile!);
      const allUploadedFiles = [...urlUploadedFiles, ...fileUploadedFiles];

      setIsSubmitting(false);
      resetState();
      onFilesSelected(allUploadedFiles);
      queryClient.invalidateQueries({
        queryKey: ['rfq', rfq.rfq.id.toString()]
      });
    } catch (error) {
      console.error('Error sending:', error);
      setIsSubmitting(false);
    }
  };

  const handleAddOwnImages = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);
    const timestamp = Date.now();
    const newPendingFiles: SelectedImage[] = fileArray.map((file, index) => {
      const imageId = `${file.name}-${file.size}-${file.lastModified}-${timestamp}-${index}-${Math.random()}`;
      const preview = URL.createObjectURL(file);

      setPreview(imageId, preview);

      return {
        url: preview,
        id: imageId,
        file,
        pendingFileId: '',
        caption: '',
        originalIndex: index,
        preview,
        source: 'user'
      };
    });

    setPendingFileUploads(newPendingFiles);
    openModalForNewItems(0);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const imageDelays = useMemo(() => {
    if (images.length === 0) return [];
    const indices = Array.from({ length: images.length }, (_, i) => i);
    const shuffled = indices.sort(() => Math.random() - 0.5);

    return shuffled.map((_, index) => index * 0.03 + Math.random() * 0.08);
  }, [images.length]);

  // Render preview for caption modal
  const renderPreview = useCallback(
    (image: SelectedImage, previewUrl: string | undefined) => {
      if (!previewUrl) return null;

      return (
        <motion.div
          key={`${image.id}-${currentCaptionIndex}`}
          layoutId={`image-${image.url}`}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{
            duration: 0.5,
            delay: 0.25,
            ease: [0.25, 0.1, 0.25, 1],
            layout: {
              type: 'spring',
              stiffness: 400,
              damping: 35
            }
          }}
          className="h-full"
        >
          <img
            draggable={false}
            src={previewUrl}
            alt={`Selected ${currentCaptionIndex + 1}`}
            className="h-full w-auto object-contain rounded-2xl border border-gray-4 bg-gray-2"
            onError={() => {}}
          />
        </motion.div>
      );
    },
    [currentCaptionIndex]
  );

  const getPreviewUrl = useCallback(
    (image: SelectedImage) => {
      return getPreview(image.id) || image.preview || image.url;
    },
    [getPreview]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 px-4 md:px-8 gap-4 md:gap-8 flex flex-col md:grid md:grid-cols-10 overflow-hidden">
        {/* Left side - selection list */}
        <div className="flex flex-col gap-3 md:gap-4 md:col-span-4 py-4 md:py-6 flex-shrink-0">
          <div>
            <Typography as="h2" variant="h5" weight="bold">
              Choose reference images
            </Typography>
            <Typography variant="body-2" className="text-secondary-text mt-1">
              Pick any examples that resemble your product, and tell us what you
              like.
            </Typography>
          </div>

          <div
            ref={captionListRef}
            className="min-h-[120px] max-h-[180px] md:min-h-[420px] md:max-h-[420px] overflow-y-auto cavela-scrollbar"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#FFF'
            }}
          >
            {selectedImages.length > 0 ? (
              <div className="flex flex-col gap-3">
                <AnimatePresence>
                  {selectedImages.map((selectedImage, index) => {
                    const pendingFile = pendingFiles.find(
                      (pf) => pf.id === selectedImage.pendingFileId
                    );
                    const currentModalItem = modalItems[currentCaptionIndex];
                    const isSelected =
                      isCaptionModalOpen &&
                      currentModalItem &&
                      currentModalItem.id === selectedImage.id;

                    return (
                      <motion.div
                        key={selectedImage.id}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        layout
                        transition={{
                          layout: {
                            duration: 0.3,
                            ease: [0.4, 0, 0.2, 1]
                          },
                          opacity: { duration: 0.2, ease: 'easeOut' },
                          y: { duration: 0.2, ease: 'easeOut' }
                        }}
                        className={cn(
                          'flex items-center gap-4 p-3 bg-gray-1 rounded-xl cursor-pointer hover:bg-gray-2 transition-colors overflow-hidden',
                          isSelected && 'bg-gray-4'
                        )}
                        onClick={() => handleEditCaption(index)}
                      >
                        {isImageFile(selectedImage) ? (
                          <img
                            src={selectedImage.preview || selectedImage.url}
                            alt={`Selected ${index + 1}`}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-2 rounded-lg flex items-center justify-center">
                            <DocumentIcon className="size-6 text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {selectedImage.caption ? (
                            <Typography variant="body-2" weight="medium">
                              {selectedImage.caption}
                            </Typography>
                          ) : (
                            <Typography
                              variant="body-2"
                              className="text-secondary-text"
                            >
                              No caption
                            </Typography>
                          )}
                          {selectedImage.file && (
                            <Typography
                              variant="body-2"
                              className="text-secondary-text truncate"
                            >
                              {selectedImage.file.name}
                            </Typography>
                          )}
                          {selectedImage.isUploading && (
                            <div className="mt-1 flex items-center gap-2">
                              <div className="size-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              <Typography
                                variant="body-2"
                                className="text-secondary-text"
                              >
                                Sending image
                              </Typography>
                            </div>
                          )}
                          {selectedImage.uploadError && (
                            <Typography
                              variant="body-2"
                              className="text-danger mt-1"
                            >
                              Upload failed: {selectedImage.uploadError}
                            </Typography>
                          )}
                          {pendingFile?.status === 'uploading' && (
                            <div className="mt-1">
                              <Progress
                                value={pendingFile.progress}
                                size="sm"
                              />
                            </div>
                          )}
                          {pendingFile?.status === 'error' && (
                            <UploadFailedNotice
                              error={pendingFile.error}
                              onRetry={() => retryUpload(pendingFile.id)}
                              onRemove={() =>
                                handleRemoveSelectedImage(selectedImage.id)
                              }
                            />
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSelectedImage(selectedImage.id);
                          }}
                          className="flex-shrink-0 p-1.5 hover:bg-gray-3 rounded-full transition-colors"
                          aria-label="Remove image"
                        >
                          <XMarkIcon className="size-4 text-gray-600" />
                        </button>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between flex-shrink-0 gap-2">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
              <ButtonV2
                onClick={handleAddOwnImages}
                size="md"
                color="tertiary"
                className="rounded-full px-3 md:px-4"
              >
                <PlusIcon className="size-4" />
                <Typography
                  variant="body-2"
                  weight="bold"
                  className="hidden sm:inline"
                >
                  Add your own images
                </Typography>
                <Typography
                  variant="body-2"
                  weight="bold"
                  className="sm:hidden"
                >
                  Add images
                </Typography>
              </ButtonV2>
            </div>
            <ButtonV2
              onPress={handleSend}
              isDisabled={
                isSubmitting ||
                isUploadingFiles ||
                selectedImages.some((img) => img.isUploading === true) ||
                selectedImages.length === 0
              }
              isLoading={isSubmitting}
              size="md"
              className="w-24 md:w-28 rounded-full px-3 md:px-4"
            >
              Submit
            </ButtonV2>
          </div>
        </div>

        {/* Right side wrapper - non-scrolling positioning context for the overlay */}
        <div className="relative md:col-span-6 overflow-hidden px-0 md:px-4 flex-1 min-h-0">
          {/* Scrollable image grid */}
          <div
            ref={imagesContainerRef}
            tabIndex={-1}
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#FFF'
            }}
            className="flex flex-col gap-4 h-full px-3 md:px-6 outline-none overflow-y-auto cavela-scrollbar"
          >
            {/* Top decorative div - scroll indicator */}
            <div
              style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 49,
                backgroundColor: 'transparent',
                backgroundImage: 'radial-gradient(transparent 1px, white 1px)',
                backgroundSize: '4px 4px',
                backdropFilter: 'blur(3px)',
                mask: 'linear-gradient(#000 calc(100% - 20px), #0000)'
              }}
            />
            <div className="relative w-full">
              {images.length > 0 ? (
                <div className="relative top-[-24px]">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                    {images
                      .filter((image) => {
                        const url =
                          typeof image === 'string' ? image : image.url;

                        return !failedImageUrls.has(url);
                      })
                      .map((image, index) => {
                        const imageUrl =
                          typeof image === 'string' ? image : image.url;

                        const isSelected = selectedImages.some(
                          (img) => img.url === imageUrl
                        );

                        const isFirstColumn = index % 3 === 0 && index !== 0;
                        const isLastColumn = index % 3 === 2 && index !== 2;
                        // Staggered margins only apply on desktop (3-col grid)
                        const marginTop = isMobile
                          ? undefined
                          : index === 1
                            ? '80px'
                            : index === 2
                              ? '34px'
                              : isLastColumn
                                ? '-46px'
                                : isFirstColumn
                                  ? '-80px'
                                  : undefined;

                        const isLoaded = loadedImageUrls.has(imageUrl);

                        const isInModal =
                          isCaptionModalOpen &&
                          (modalItems[currentCaptionIndex]?.url === imageUrl ||
                            pendingImage?.url === imageUrl);

                        return (
                          <motion.div
                            key={imageUrl}
                            layoutId={
                              isSelected || isInModal
                                ? `image-${imageUrl}`
                                : undefined
                            }
                            initial={{ opacity: 0 }}
                            animate={{
                              opacity: isInModal ? 0 : 1,
                              scale: isInModal ? 0.98 : 1
                            }}
                            transition={{
                              duration: 0.7,
                              delay: imageDelays[index] || 0,
                              ease: [0.4, 0, 0.2, 1],
                              layout: {
                                type: 'spring',
                                stiffness: 400,
                                damping: 35
                              },
                              opacity: {
                                duration: isInModal ? 0.15 : 0.7,
                                ease: [0.4, 0, 0.2, 1]
                              },
                              scale: {
                                duration: 0.15,
                                ease: [0.4, 0, 0.2, 1]
                              }
                            }}
                            onClick={(e) => handleImageClick(e, image, index)}
                            className={cn(
                              'border border-gray-4 relative overflow-hidden cursor-pointer hover:opacity-80 transition-all bg-gray-2 rounded-xl',
                              isSelected
                                ? 'shadow-[0_4px_6px_-1px_rgba(0,14,239,0.3),0_2px_4px_-1px_rgba(0,14,239,0.2)] border-primary'
                                : '',
                              isInModal && 'pointer-events-none z-50'
                            )}
                            style={{
                              height: isMobile ? '150px' : '200px',
                              ...(marginTop && { marginTop })
                            }}
                          >
                            <div className="relative w-full h-full">
                              <motion.div
                                className="absolute inset-0 w-full h-full rounded-lg"
                                initial={{
                                  filter: 'blur(20px)',
                                  scale: 1.1
                                }}
                                animate={{
                                  filter: isLoaded ? 'blur(0px)' : 'blur(20px)',
                                  scale: isLoaded ? 1 : 1.1
                                }}
                                transition={{
                                  duration: 0.8,
                                  ease: [0.4, 0, 0.2, 1]
                                }}
                                style={{
                                  backgroundImage: `url(${imageUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center'
                                }}
                              />
                              <motion.div
                                className="absolute inset-0 w-full h-full"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: isLoaded ? 1 : 0 }}
                                transition={{
                                  duration: 0.6,
                                  ease: [0.4, 0, 0.2, 1]
                                }}
                              >
                                <img
                                  draggable={false}
                                  src={imageUrl}
                                  alt={imageUrl || `Search result ${index + 1}`}
                                  className="w-full h-full object-cover max-w-full max-h-full"
                                  onLoad={(e) =>
                                    handleImageLoadOrSizeCheck(imageUrl, e)
                                  }
                                  onError={() => handleImageError(imageUrl)}
                                />
                              </motion.div>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>

                  {/* Refine search button */}
                  {!showMoreInput && (
                    <div className="sticky bottom-20 flex justify-end mt-4 z-10">
                      <ButtonV2
                        value={searchQuery}
                        onPress={handleOpenRefineSearch}
                        size="md"
                        className="h-12 rounded-full px-4 text-dark border border-white bg-[#F9FAFB66]/80 backdrop-blur-md shadow-md hover:!opacity-100 hover:!bg-[#F9FAFB66]/85 hover:shadow-[0_4px_20px_rgba(0,14,239,0.15)] !transition-all"
                        endContent={
                          <div className="min-h-8 max-h-8 min-w-8 max-w-8 bg-white rounded-full flex items-center justify-center">
                            {imageSearch.isFetching ? (
                              <Spinner size="sm" />
                            ) : (
                              <StarIcon className="size-4" />
                            )}
                          </div>
                        }
                      >
                        Refine search
                      </ButtonV2>
                    </div>
                  )}
                </div>
              ) : rateLimitError ||
                (imageSearch.isError && currentSearchQuery) ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 py-20">
                  <Typography
                    variant="body-1"
                    weight="medium"
                    className="text-red-600"
                  >
                    {rateLimitError ||
                      imageSearch.error?.message ||
                      'Failed to fetch images'}
                  </Typography>
                  <Typography variant="body-2" className="text-gray-6">
                    Please try again later
                  </Typography>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full min-h-[300px]">
                  <BlobV2
                    speed={0.2}
                    numBlobs={6}
                    isolation={70}
                    lookAt={{ x: 0, y: 500, z: 0 }}
                    zoom={1}
                  />
                </div>
              )}
            </div>

            {/* Search input overlay */}
            <AnimatePresence>
              {showMoreInput && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="absolute inset-0 z-[9999] flex items-center justify-center"
                  onClick={(e) => {
                    if (e.target === e.currentTarget && hasSearched) {
                      handleCloseRefineSearch();
                    }
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.1,
                      ease: [0.4, 0, 0.2, 1]
                    }}
                    className={cn(
                      'relative bg-[#F9FAFB66]/30 backdrop-blur-md w-full h-full p-6 flex flex-col items-center justify-center gap-6 overflow-y-auto',
                      hasSearched && 'cursor-pointer'
                    )}
                    onClick={(e) => {
                      if (hasSearched) {
                        handleCloseRefineSearch();
                      }
                      e.stopPropagation();
                    }}
                  >
                    {hasSearched && (
                      <button
                        onClick={handleCloseRefineSearch}
                        className="absolute top-5 right-5 p-2 hover:bg-white/20 rounded-full transition-colors z-10"
                        aria-label="Close"
                      >
                        <XMarkIcon className="size-8 text-gray-700" />
                      </button>
                    )}

                    <div
                      ref={searchInputWrapperRef}
                      className="bg-white rounded-full px-3 md:px-4 py-2 md:py-3 shadow-lg flex items-center gap-2 md:gap-3 w-full max-w-md"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={`Photos of ${rfq?.rfq?.document?.name}`}
                        classNames={{
                          input:
                            '!bg-transparent border-none focus:outline-none',
                          inputWrapper:
                            '!bg-transparent border-none shadow-none hover:!bg-transparent focus-within:!bg-transparent'
                        }}
                        className="flex-1"
                        autoFocus
                      />
                      <ButtonV2
                        onPress={handleSearch}
                        isIconOnly
                        size="sm"
                        className="!w-8 !h-8 !min-w-8 !min-h-8 rounded-full bg-primary-text"
                      >
                        <StarIcon className="size-4 text-white" />
                      </ButtonV2>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Bottom decorative div - scroll indicator */}
            <div
              style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 10,
                backgroundColor: 'transparent',
                backgroundImage: 'radial-gradient(transparent 1px, white 1px)',
                backgroundSize: '4px 4px',
                backdropFilter: 'blur(3px)',
                mask: 'linear-gradient(#0000 0%, #000 20px)',
                pointerEvents: 'none'
              }}
            />
          </div>

          {/* Inline Caption Overlay - sits outside scroll container, stays fixed on top */}
          <InlineCaptionOverlay
            isOpen={isCaptionModalOpen}
            onClose={handleCaptionModalClose}
            items={modalItems}
            currentIndex={currentCaptionIndex}
            captionValue={captionValue}
            onCaptionChange={setCaptionValue}
            isEditMode={isEditMode}
            onSave={handleCaptionSave}
            onNext={handleCaptionNext}
            onPrevious={handleCaptionPrevious}
            onDone={handleCaptionDone}
            renderPreview={renderPreview}
            getPreviewUrl={getPreviewUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default AddReferencesImageTab;
